import { Container, Graphics, Text } from 'pixi.js';

import { BaseWidget } from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';

/**
 * Heading tape widget that displays a scrolling horizontal tape similar to aviation instruments.
 * Shows major graduations every 10 degrees with labels, extended ticks every 5 degrees,
 * and minor tick marks every degree.
 * The tape scrolls horizontally as heading changes, with approximately 35 degrees visible in the viewport.
 */
export class HeadingTapeWidget extends BaseWidget {
    static readonly requiredProperties = ['magneticHeading'] as const;
    static readonly optionalProperties = [] as const;

    private tapeGraphics: Graphics;
    private labelsContainer: Container;
    private centerIndicator: Container;
    private centerHeadingText: Text;
    private purpleMarker: Graphics;

    // Configuration constants
    private readonly PIXELS_PER_DEGREE = 5; // Scale factor for tape movement (higher = more zoom)
    private readonly MAJOR_INTERVAL = 10; // Major graduations every 10 degrees
    private readonly MEDIUM_INTERVAL = 5; // Medium graduations every 5 degrees
    private readonly MINOR_INTERVAL = 1; // Minor tick marks every degree
    private readonly TAPE_BUFFER = 60; // Draw this many degrees left/right of viewport
    private readonly MAJOR_TICK_HEIGHT = 8;
    private readonly MINOR_TICK_HEIGHT = 4;
    private readonly TICK_LABEL_COLOR = 0xffffff; // Color for ticks and labels

    // Center indicator constants
    private readonly CENTER_BOX_WIDTH = 24;
    private readonly CENTER_BOX_HEIGHT = 12;
    private readonly CENTER_ARROW_SIZE = 5;
    private readonly CENTER_Y_POSITION = 0.3;

    // Purple marker constants
    private readonly PURPLE_MARKER_COLOR = 0x9370db;
    private readonly PURPLE_MARKER_SIZE = 5;

    // Layout constants
    private readonly LABEL_OFFSET = 2;

    private currentHeading = 0;
    private lastDrawnHeading = 0;

    async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        // Create graphics object for the tape
        this.tapeGraphics = new Graphics();
        this.labelsContainer = new Container();

        // Create viewport mask to clip the tape to the visible area
        const viewportMask = new Graphics()
            .rect(
                positionTranslator.x(0),
                positionTranslator.y(0),
                positionTranslator.x(1),
                positionTranslator.y(1)
            )
            .fill(0xffffff);
        elementContainer.addChild(viewportMask);
        elementContainer.mask = viewportMask;

        // Add tape and labels to container
        elementContainer.addChild(this.tapeGraphics);
        elementContainer.addChild(this.labelsContainer);

        // Create static center indicator (black box with current heading)
        this.centerIndicator = new Container();
        const centerX = positionTranslator.x(0.5);
        const centerY = positionTranslator.y(this.CENTER_Y_POSITION);

        // Draws heading indicator box with arrow
        const combinedShape = new Graphics();

        combinedShape
            .moveTo(-this.CENTER_BOX_WIDTH / 2, -this.CENTER_BOX_HEIGHT / 2)
            .lineTo(this.CENTER_BOX_WIDTH / 2, -this.CENTER_BOX_HEIGHT / 2) // Top edge
            .lineTo(this.CENTER_BOX_WIDTH / 2, this.CENTER_BOX_HEIGHT / 2) // Right edge
            .lineTo(this.CENTER_ARROW_SIZE, this.CENTER_BOX_HEIGHT / 2) // Bottom right to arrow base
            .lineTo(0, this.CENTER_BOX_HEIGHT / 2 + this.CENTER_ARROW_SIZE) // Arrow point
            .lineTo(-this.CENTER_ARROW_SIZE, this.CENTER_BOX_HEIGHT / 2) // Arrow left side
            .lineTo(-this.CENTER_BOX_WIDTH / 2, this.CENTER_BOX_HEIGHT / 2) // Bottom left
            .lineTo(-this.CENTER_BOX_WIDTH / 2, -this.CENTER_BOX_HEIGHT / 2); // Left edge back to start

        combinedShape.fill(0x000000).stroke({ width: 1, color: 0xffffff });

        // Heading text
        this.centerHeadingText = new Text({
            text: '000',
            style: {
                fontFamily: hudContext.globalConfig.fontFamily,
                fontSize: hudContext.globalConfig.baseFontSize,
                fill: 0xffffff,
                align: 'center',
            },
        });
        this.centerHeadingText.anchor.set(0.5, 0.5);

        this.centerIndicator.addChild(combinedShape);
        this.centerIndicator.addChild(this.centerHeadingText);
        this.centerIndicator.position.set(centerX, centerY);

        elementContainer.addChild(this.centerIndicator);

        // Create static purple triangle marker at current heading tick position
        const tapeBottom = positionTranslator.y(1);
        this.purpleMarker = new Graphics()
            .moveTo(0, -this.PURPLE_MARKER_SIZE) // Top point
            .lineTo(-this.PURPLE_MARKER_SIZE, 0) // Bottom left
            .lineTo(this.PURPLE_MARKER_SIZE, 0) // Bottom right
            .lineTo(0, -this.PURPLE_MARKER_SIZE) // Back to top
            .fill(this.PURPLE_MARKER_COLOR)
            .stroke({ width: 1, color: 0x000000 }); // Black outline

        this.purpleMarker.position.set(centerX, tapeBottom);
        elementContainer.addChild(this.purpleMarker);
    }

    protected updateWidget(
        point: CurrentPoint,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        this.currentHeading = point.magneticHeading;

        // Update center heading display
        const normalizedHeading = HeadingTapeWidget.normalizeHeading(
            this.currentHeading
        );
        this.centerHeadingText.text = Math.round(normalizedHeading)
            .toString()
            .padStart(3, '0');

        // Redraw the tape if heading has changed significantly or if this is the first draw
        if (
            this.lastDrawnHeading === 0 ||
            HeadingTapeWidget.getHeadingDifference(
                this.currentHeading,
                this.lastDrawnHeading
            ) > 30
        ) {
            this.drawTape(hudContext, positionTranslator);
            this.lastDrawnHeading = this.currentHeading;
        }

        // Update tape position to create scrolling effect
        this.updateTapePosition();
    }

    /**
     * Normalize heading to 0-360 range.
     *
     * @param heading - The heading to normalize (can be any value)
     * @returns Normalized heading in the range [0, 360)
     *
     * @example
     * normalizeHeading(-10)  // returns 350
     * normalizeHeading(370)  // returns 10
     * normalizeHeading(720)  // returns 0
     */
    public static normalizeHeading(heading: number): number {
        let normalized = heading % 360;
        if (normalized < 0) normalized += 360;
        return normalized;
    }

    /**
     * Normalize a heading difference to -180 to 180 range (shortest path).
     * This is critical for calculating the shortest angular distance between headings.
     *
     * @param diff - The heading difference to normalize
     * @returns Normalized difference in the range (-180, 180]
     *
     * @example
     * normalizeHeadingDifference(270)   // returns -90 (shorter to go left)
     * normalizeHeadingDifference(-270)  // returns 90 (shorter to go right)
     * normalizeHeadingDifference(180)   // returns 180 (ambiguous, but stays 180)
     */
    public static normalizeHeadingDifference(diff: number): number {
        let normalized = diff;
        while (normalized > 180) normalized -= 360;
        while (normalized < -180) normalized += 360;
        return normalized;
    }

    /**
     * Calculate the shortest difference between two headings, accounting for 0/360 wrap.
     * Always returns a positive value (absolute difference).
     *
     * @param heading1 - First heading
     * @param heading2 - Second heading
     * @returns Absolute difference between 0 and 180 degrees
     *
     * @example
     * getHeadingDifference(350, 10)  // returns 20 (not 340!)
     * getHeadingDifference(10, 350)  // returns 20 (same result)
     * getHeadingDifference(0, 180)   // returns 180
     */
    public static getHeadingDifference(
        heading1: number,
        heading2: number
    ): number {
        return Math.abs(
            HeadingTapeWidget.normalizeHeadingDifference(heading1 - heading2)
        );
    }

    /**
     * Get display label for a heading.
     * Returns cardinal directions (N, E, S, W) for 0°, 90°, 180°, 270°,
     * otherwise returns the heading as a string.
     *
     * @param heading - The heading to get a label for
     * @returns Label string (e.g., "N", "E", "45", "270")
     *
     * @example
     * getHeadingLabel(0)    // returns "N"
     * getHeadingLabel(90)   // returns "E"
     * getHeadingLabel(180)  // returns "S"
     * getHeadingLabel(270)  // returns "W"
     * getHeadingLabel(360)  // returns "N"
     * getHeadingLabel(45)   // returns "45"
     */
    public static getHeadingLabel(heading: number): string {
        if (heading === 0 || heading === 360) return 'N';
        if (heading === 90) return 'E';
        if (heading === 180) return 'S';
        if (heading === 270) return 'W';
        return heading.toString();
    }

    /**
     * Draws the heading tape with major, medium, and minor graduations.
     * Major graduations (every 10 degrees) have extended ticks and labels.
     * Medium graduations (every 5 degrees) have extended ticks without labels.
     * Minor graduations (every 1 degree) have short ticks without labels.
     * Draws a range centered on current heading to provide buffer for scrolling.
     */
    private drawTape(
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        // Clear existing graphics
        this.tapeGraphics.clear();
        this.labelsContainer.removeChildren();

        const centerX = positionTranslator.x(0.5);
        const tapeBottom = positionTranslator.y(1); // Tick marks at bottom

        // Calculate heading range to draw (can go beyond 0/360)
        const minHeading = this.currentHeading - this.TAPE_BUFFER;
        const maxHeading = this.currentHeading + this.TAPE_BUFFER;

        // Draw all graduations
        for (
            let heading =
                Math.floor(minHeading / this.MINOR_INTERVAL) *
                this.MINOR_INTERVAL;
            heading <= maxHeading;
            heading += this.MINOR_INTERVAL
        ) {
            const normalizedHeading =
                HeadingTapeWidget.normalizeHeading(heading);
            const isMajor = normalizedHeading % this.MAJOR_INTERVAL === 0;
            const isMedium =
                !isMajor && normalizedHeading % this.MEDIUM_INTERVAL === 0;

            // Determine tick height: major and medium both use extended height
            const tickHeight =
                isMajor || isMedium
                    ? this.MAJOR_TICK_HEIGHT
                    : this.MINOR_TICK_HEIGHT;

            // Calculate X position relative to center
            const headingDiff = heading - this.currentHeading;
            const x = centerX + headingDiff * this.PIXELS_PER_DEGREE;

            // Add tick mark - extend UP from bottom
            this.tapeGraphics.moveTo(x, tapeBottom);
            this.tapeGraphics.lineTo(x, tapeBottom - tickHeight);

            // Draw label for major graduations only - position ABOVE tick
            if (isMajor) {
                const labelText =
                    HeadingTapeWidget.getHeadingLabel(normalizedHeading);
                const label = new Text({
                    text: labelText,
                    style: {
                        fontFamily: hudContext.globalConfig.fontFamily,
                        fontSize: hudContext.globalConfig.baseFontSize + 1,
                        fill: this.TICK_LABEL_COLOR,
                    },
                });
                label.anchor.set(0.5, 1); // Center horizontally, bottom align
                label.position.set(
                    x,
                    tapeBottom - tickHeight - this.LABEL_OFFSET
                );
                this.labelsContainer.addChild(label);
            }
        }

        // Stroke all the lines at once
        this.tapeGraphics.stroke({ width: 1, color: this.TICK_LABEL_COLOR });
    }

    /**
     * Updates the position of the tape graphics to create scrolling effect.
     * The tape moves left as heading increases (turning right) and right as heading decreases (turning left).
     */
    private updateTapePosition(): void {
        const headingDiff = HeadingTapeWidget.normalizeHeadingDifference(
            this.currentHeading - this.lastDrawnHeading
        );
        const offset = -headingDiff * this.PIXELS_PER_DEGREE; // Negative because tape moves opposite to heading change

        // Move both the graphics and labels together
        this.tapeGraphics.position.x = offset;
        this.labelsContainer.position.x = offset;
    }

    cleanUp(): void {
        this.purpleMarker?.destroy();
        this.centerHeadingText?.destroy();
        this.centerIndicator?.destroy();
        this.tapeGraphics?.destroy();
        this.labelsContainer?.destroy();

        this.purpleMarker = null;
        this.centerHeadingText = null;
        this.centerIndicator = null;
        this.tapeGraphics = null;
        this.labelsContainer = null;
    }
}
